import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney, formatDate, todayLocalIso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Wallet, ArrowRightLeft, TrendingUp, TrendingDown, Pencil, Trash2 } from "lucide-react";

export default function Cajas() {
  const [items, setItems] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ nombre: "", responsable: "", saldo_inicial: 0, activo: true });
  const [editCaja, setEditCaja] = useState(null); // {id, nombre, responsable, saldo_inicial, activo}
  const [delCaja, setDelCaja] = useState(null);

  const [openMov, setOpenMov] = useState(null); // caja seleccionada para movimiento
  const [mov, setMov] = useState({ tipo: "ingreso", monto: "", motivo: "", origen: "", referencia: "", fecha: todayLocalIso() });

  const [openTrf, setOpenTrf] = useState(false);
  const [trf, setTrf] = useState({ origen_tipo: "caja", origen_id: "", destino_tipo: "caja", destino_id: "", monto: "", motivo: "", fecha: todayLocalIso() });

  const load = async () => {
    try { const { data } = await api.get("/cajas"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const createCaja = async () => {
    if (!form.nombre.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      await api.post("/cajas", form);
      toast.success("Caja creada"); setOpenNew(false); setForm({ nombre:"", responsable:"", saldo_inicial:0, activo:true }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveMov = async () => {
    if (!openMov) return;
    const monto = Number(mov.monto);
    if (!monto || monto <= 0) { toast.error("Monto inválido"); return; }
    try {
      await api.post("/cajas/movimientos", { ...mov, monto, caja_id: openMov.id });
      toast.success("Movimiento registrado");
      setOpenMov(null); setMov({ tipo:"ingreso", monto:"", motivo:"", origen:"", referencia:"", fecha: todayLocalIso() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveTrf = async () => {
    const monto = Number(trf.monto);
    if (!monto || monto <= 0) { toast.error("Monto inválido"); return; }
    if (!trf.origen_id || !trf.destino_id) { toast.error("Seleccioná origen y destino"); return; }
    try {
      await api.post("/cajas/transferencias", { ...trf, monto });
      toast.success("Transferencia registrada");
      setOpenTrf(false); setTrf({ origen_tipo:"caja", origen_id:"", destino_tipo:"caja", destino_id:"", monto:"", motivo:"", fecha: todayLocalIso() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="cajas-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Finanzas</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Wallet className="w-8 h-8" /> Cajas
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Efectivo y cajas físicas — saldos, ingresos, egresos y transferencias.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpenTrf(true)} className="gap-2" data-testid="new-trf-btn">
            <ArrowRightLeft className="w-4 h-4" /> Transferencia
          </Button>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-caja-btn">
                <Plus className="w-4 h-4" /> Nueva caja
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nueva caja</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="caja-nombre" /></div>
                <div><Label>Responsable</Label><Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} data-testid="caja-responsable" /></div>
                <div><Label>Saldo inicial (Q)</Label><Input type="number" step="0.01" value={form.saldo_inicial} onChange={(e) => setForm({ ...form, saldo_inicial: Number(e.target.value) })} /></div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div className="text-sm font-semibold">Activa</div>
                  <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenNew(false)}>Cancelar</Button>
                <Button onClick={createCaja} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-caja-btn">Crear</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <Card key={c.id} className="p-5 border-zinc-200 rounded-md" data-testid={`caja-card-${c.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-lg">{c.nombre}</div>
              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${c.activo ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                {c.activo ? "Activa" : "Inactiva"}
              </span>
            </div>
            {c.responsable && <div className="text-xs text-zinc-500 mb-3">Responsable: {c.responsable}</div>}
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Saldo disponible</div>
            <div className={`font-manrope font-black text-3xl mt-1 tabular-nums ${c.saldo_disponible < 0 ? "text-red-600" : "text-zinc-900"}`} data-testid={`caja-saldo-${c.id}`}>
              {formatMoney(c.saldo_disponible)}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              <div><span className="text-zinc-500">Inicial: </span><span className="font-semibold">{formatMoney(c.saldo_inicial)}</span></div>
              <div><span className="text-zinc-500">Ingresos: </span><span className="font-semibold text-emerald-700">+{formatMoney(c.total_ingresado)}</span></div>
              <div><span className="text-zinc-500">Egresos: </span><span className="font-semibold text-red-600">-{formatMoney(c.total_utilizado)}</span></div>
              <div><span className="text-zinc-500">Trf +: </span><span className="font-semibold text-blue-700">{formatMoney(c.total_transf_in)}</span></div>
              <div><span className="text-zinc-500">Trf -: </span><span className="font-semibold text-orange-600">{formatMoney(c.total_transf_out)}</span></div>
              <div><span className="text-zinc-500">Ajustes: </span><span className="font-semibold">{formatMoney(c.total_ajustes)}</span></div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="gap-1 flex-1" onClick={() => { setOpenMov(c); setMov({ ...mov, tipo:"ingreso" }); }} data-testid={`ingreso-btn-${c.id}`}>
                <TrendingUp className="w-3 h-3" /> Ingresar
              </Button>
              <Button size="sm" variant="outline" className="gap-1 flex-1" onClick={() => { setOpenMov(c); setMov({ ...mov, tipo:"egreso" }); }} data-testid={`egreso-btn-${c.id}`}>
                <TrendingDown className="w-3 h-3" /> Egresar
              </Button>
            </div>
            <div className="flex gap-2 mt-2 pt-2 border-t border-zinc-100">
              <Button size="sm" variant="ghost" className="gap-1 flex-1 text-zinc-600" onClick={() => setEditCaja({ ...c })} data-testid={`edit-caja-${c.id}`}>
                <Pencil className="w-3 h-3" /> Editar
              </Button>
              <Button size="sm" variant="ghost" className="gap-1 flex-1 text-red-600 hover:bg-red-50" onClick={() => setDelCaja(c)} data-testid={`del-caja-${c.id}`}>
                <Trash2 className="w-3 h-3" /> Eliminar
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-10 text-center text-zinc-400 col-span-full">Sin cajas creadas. Creá una para comenzar.</Card>
        )}
      </div>

      {/* Movimiento (ingreso/egreso) */}
      <Dialog open={!!openMov} onOpenChange={(v) => !v && setOpenMov(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{mov.tipo === "ingreso" ? "Ingresar a" : "Utilizar de"} {openMov?.nombre}</DialogTitle>
            <DialogDescription>Saldo actual: {formatMoney(openMov?.saldo_disponible || 0)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Fecha</Label><Input type="date" value={mov.fecha} onChange={(e) => setMov({ ...mov, fecha: e.target.value })} /></div>
            <div><Label>Monto (Q)</Label><Input type="number" step="0.01" value={mov.monto} onChange={(e) => setMov({ ...mov, monto: e.target.value })} data-testid="mov-monto" /></div>
            <div><Label>Motivo</Label><Input value={mov.motivo} onChange={(e) => setMov({ ...mov, motivo: e.target.value })} data-testid="mov-motivo" /></div>
            <div><Label>Origen / referencia</Label><Input value={mov.origen} onChange={(e) => setMov({ ...mov, origen: e.target.value })} placeholder="Depósito, cliente X, etc." /></div>
            <div><Label>N° referencia</Label><Input value={mov.referencia} onChange={(e) => setMov({ ...mov, referencia: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenMov(null)}>Cancelar</Button>
            <Button onClick={saveMov} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-mov-btn">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transferencia */}
      <Dialog open={openTrf} onOpenChange={setOpenTrf}>
        <DialogContent>
          <DialogHeader><DialogTitle>Transferencia</DialogTitle><DialogDescription>Entre cajas (o caja ↔ banco).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Origen</Label>
                <Select value={trf.origen_id} onValueChange={(v) => setTrf({ ...trf, origen_id: v })}>
                  <SelectTrigger data-testid="trf-origen"><SelectValue placeholder="Seleccionar caja" /></SelectTrigger>
                  <SelectContent>{items.filter(x => x.activo).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre} (Q{c.saldo_disponible})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destino</Label>
                <Select value={trf.destino_id} onValueChange={(v) => setTrf({ ...trf, destino_id: v })}>
                  <SelectTrigger data-testid="trf-destino"><SelectValue placeholder="Seleccionar caja" /></SelectTrigger>
                  <SelectContent>{items.filter(x => x.activo && x.id !== trf.origen_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Monto (Q)</Label><Input type="number" step="0.01" value={trf.monto} onChange={(e) => setTrf({ ...trf, monto: e.target.value })} data-testid="trf-monto" /></div>
            <div><Label>Motivo</Label><Input value={trf.motivo} onChange={(e) => setTrf({ ...trf, motivo: e.target.value })} /></div>
            <div><Label>Fecha</Label><Input type="date" value={trf.fecha} onChange={(e) => setTrf({ ...trf, fecha: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenTrf(false)}>Cancelar</Button>
            <Button onClick={saveTrf} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-trf-btn">Transferir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar caja */}
      <Dialog open={!!editCaja} onOpenChange={(v) => !v && setEditCaja(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar caja</DialogTitle></DialogHeader>
          {editCaja && (
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={editCaja.nombre} onChange={(e) => setEditCaja({ ...editCaja, nombre: e.target.value })} data-testid="edit-caja-nombre" /></div>
              <div><Label>Responsable</Label><Input value={editCaja.responsable || ""} onChange={(e) => setEditCaja({ ...editCaja, responsable: e.target.value })} /></div>
              <div><Label>Saldo inicial (Q)</Label><Input type="number" step="0.01" value={editCaja.saldo_inicial || 0} onChange={(e) => setEditCaja({ ...editCaja, saldo_inicial: Number(e.target.value) })} /></div>
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div className="text-sm font-semibold">Activa</div>
                <Switch checked={editCaja.activo !== false} onCheckedChange={(v) => setEditCaja({ ...editCaja, activo: v })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCaja(null)}>Cancelar</Button>
            <Button
              className="bg-[#002FA7] hover:bg-[#00227A]"
              onClick={async () => {
                if (!editCaja.nombre.trim()) return toast.error("Nombre obligatorio");
                try {
                  await api.patch(`/cajas/${editCaja.id}`, {
                    nombre: editCaja.nombre,
                    responsable: editCaja.responsable || "",
                    saldo_inicial: Number(editCaja.saldo_inicial || 0),
                    activo: editCaja.activo !== false,
                  });
                  toast.success("Caja actualizada");
                  setEditCaja(null); load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="save-edit-caja"
            >Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Eliminar caja */}
      <Dialog open={!!delCaja} onOpenChange={(v) => !v && setDelCaja(null)}>
        <DialogContent data-testid="dialog-del-caja">
          <DialogHeader>
            <DialogTitle>Eliminar caja</DialogTitle>
            <DialogDescription>
              ¿Confirmás la eliminación de <b>{delCaja?.nombre}</b>? La caja debe tener saldo cero (transferí primero o ajustá saldos).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelCaja(null)}>Cancelar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                try {
                  await api.delete(`/cajas/${delCaja.id}`);
                  toast.success("Caja eliminada");
                  setDelCaja(null); load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="confirm-del-caja"
            >Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
